import React, { Suspense, useMemo, useState } from 'react';
import {
  Button,
  dayjs,
  FIELD_BOX_CLASS,
  FIELD_LABEL_CLASS,
  fieldClass,
  lazyWithRetry,
  snapScheduledEndDate,
} from '@charitystack/shared';
import { loadScheduledPicker } from './loadScheduledPicker.js';
import PaneSheet from '../Components/PaneSheet';

export const SCHEDULE_SHEET_TITLE = 'Schedule start / end date';
import {
  calculateScheduledDates,
  frequencyToTimePicker,
  getScheduleConfigForFrequency,
  hasScheduledSelection,
} from './scheduleConfigUtils';

const ControlledRecurringDatePicker = lazyWithRetry(loadScheduledPicker, 'ControlledRecurringDatePicker');

// Mirror the closed field structure, not a guessed fixed pixel height. These
// inert frames take the same label and field recipes as the picker (shared
// controlRecipes), so there is no visible jump when the lazy picker chunk
// arrives and swaps in for real.
const PickerFieldPlaceholder = () => {
  const labelClass = `${FIELD_LABEL_CLASS} mb-[var(--cs-space-2)] block`;
  const frameClass = `dynamic-input-text flex items-center ${fieldClass(false)} ${FIELD_BOX_CLASS}`;

  return (
    <div aria-label="Loading date picker" aria-busy="true" className="flex w-full flex-col gap-3">
      <div className="@container" aria-hidden="true">
      <div className="grid grid-cols-1 gap-3 @xs:grid-cols-2">
        {['Start date', 'End date'].map(label => (
          <div key={label} className="flex min-w-0 flex-col">
            <div className={labelClass}>{label}</div>
            <div className={frameClass}>
              <span className="text-[color:var(--cs-text-tertiary)]">{label === 'Start date' ? 'Today' : 'Select'}</span>
            </div>
          </div>
        ))}
      </div>
      </div>
      <div className="flex flex-col" aria-hidden="true">
        <div className={labelClass}>Time</div>
        <div className={frameClass}>
          <span>Now</span>
        </div>
      </div>
    </div>
  );
};

const buildInitialDraft = ({ startDate, endDate, timeValue, defaultScheduledRange, selectedFrequency }) => {
  if (hasScheduledSelection({ startDate, endDate, timeValue })) {
    return {
      startDate: startDate || null,
      endDate: endDate || null,
      timeValue: timeValue || 'now',
    };
  }

  const scheduleDefaults = calculateScheduledDates(
    getScheduleConfigForFrequency(defaultScheduledRange, selectedFrequency),
    selectedFrequency,
  );

  return {
    startDate: scheduleDefaults.startDate || null,
    endDate: scheduleDefaults.endDate || null,
    timeValue: scheduleDefaults.timeValue || 'now',
  };
};

const mapFrequencyToTimePicker = frequencyToTimePicker;

/**
 * The schedule editor, in a sheet over the form card (owner call 2026-10-21; was its own form
 * step). It keeps a draft of the start date, end date and time, validates it, and hands the
 * result to `onSave`; closing (`onCancel`: Escape, the close button, the scrim) drops the draft.
 * Mount it with `isActive` while the sheet is open — the picker chunk loads only then — and
 * `container`, the card it covers, from usePaneSheet (Components/PaneSheet.jsx). The standard,
 * crowdfunding and product forms all open it from the "Schedule start / end date" link.
 */
const ScheduleSheetBody = ({
  container = null,
  brandColors,
  selectedFrequency,
  startDate,
  endDate,
  timeValue,
  defaultScheduledRange,
  startDateError = '',
  endDateError = '',
  timeError = '',
  onSave,
  onValidationError,
  onCancel,
}) => {
  // The draft is built once, from the form's saved values, when the sheet opens: this body
  // mounts on open and unmounts on close, so a draft never outlives its session and a
  // cleared / re-fitted / removed schedule cannot resurface on the next open (QA round 2, F2).
  const [initialDraft] = useState(() => buildInitialDraft({
    startDate,
    endDate,
    timeValue,
    defaultScheduledRange,
    selectedFrequency,
  }));
  const [draftStartDate, setDraftStartDate] = useState(initialDraft.startDate);
  const [draftEndDate, setDraftEndDate] = useState(initialDraft.endDate);
  const [draftTimeValue, setDraftTimeValue] = useState(initialDraft.timeValue);
  const [localStartDateError, setLocalStartDateError] = useState(startDateError);
  const [localEndDateError, setLocalEndDateError] = useState(endDateError);
  const [localTimeError, setLocalTimeError] = useState(timeError);

  const startDateObj = useMemo(
    () => (draftStartDate ? dayjs(draftStartDate) : undefined),
    [draftStartDate],
  );
  const endDateObj = useMemo(
    () => (draftEndDate ? dayjs(draftEndDate) : undefined),
    [draftEndDate],
  );

  // The picker snaps the end date onto the start's recurrence when it mounts.
  // It loads on demand, so a Save can arrive first: apply the same clamp here,
  // so the saved schedule is the one the picker would have shown.
  // With no start date the schedule starts today and a chosen end after today
  // stays put (the picker does the same), so clearing the start never moves it.
  const clampedDraftEndDate = () => {
    const today = dayjs().startOf('day');
    if (!draftStartDate && draftEndDate && dayjs(draftEndDate).startOf('day').isAfter(today)) {
      return draftEndDate;
    }
    return snapScheduledEndDate({
      startValue: draftStartDate,
      endValue: draftEndDate,
      timePicker: mapFrequencyToTimePicker(selectedFrequency),
      today,
    }) ?? draftEndDate;
  };

  const validateDraft = (endDateToSave) => {
    let hasError = false;
    const today = dayjs().startOf('day');
    let nextStartDateError = '';
    let nextEndDateError = '';
    let nextTimeError = '';

    setLocalStartDateError('');
    setLocalEndDateError('');
    setLocalTimeError('');

    if (draftStartDate) {
      const draftStartDay = dayjs(draftStartDate).startOf('day');
      if (draftStartDay.isBefore(today, 'day')) {
        nextStartDateError = 'Please input a date that is today or in the future';
        hasError = true;
      }
    }

    if (endDateToSave) {
      const draftEndDay = dayjs(endDateToSave).startOf('day');
      if (draftEndDay.isBefore(today, 'day')) {
        nextEndDateError = 'Please input an end date that is today or in the future';
        hasError = true;
      }
    }

    if (draftStartDate && !draftTimeValue) {
      nextTimeError = 'Please select a time for your scheduled donation';
      hasError = true;
    }

    setLocalStartDateError(nextStartDateError);
    setLocalEndDateError(nextEndDateError);
    setLocalTimeError(nextTimeError);

    if (hasError) {
      onValidationError?.({
        startDateError: nextStartDateError,
        endDateError: nextEndDateError,
        timeError: nextTimeError,
      });
    }

    return !hasError;
  };

  const handleSave = () => {
    if (!draftStartDate && !draftEndDate) {
      onSave?.({
        startDate: null,
        endDate: null,
        timeValue: null,
      });
      return;
    }

    const endDateToSave = clampedDraftEndDate();
    if (!validateDraft(endDateToSave)) {
      return;
    }

    onValidationError?.({
      startDateError: '',
      endDateError: '',
      timeError: '',
    });
    onSave?.({
      startDate: draftStartDate,
      // A fresh value on every save, so the form treats it as the donor's own choice (the
      // frequency section's re-fit source compares by identity).
      endDate: endDateToSave ? dayjs(endDateToSave) : endDateToSave,
      timeValue: draftTimeValue || 'now',
    });
  };

  return (
    <PaneSheet
      title={SCHEDULE_SHEET_TITLE}
      closeLabel="Close"
      onClose={onCancel}
      container={container}
      // Fits its few fields instead of leaving a blank stretch above Save.
      height="content"
      footer={
        <Button
          type="button"
          family="cta"
          variant="cta"
          size="cta"
          className="dynamic-button-text"
          onClick={handleSave}
        >
          Save
        </Button>
      }
    >
      <div className="pt-[var(--cs-space-5)]">
        <Suspense fallback={<PickerFieldPlaceholder />}>
          <ControlledRecurringDatePicker
            theme="embed_form"
            fundKey={0}
            startValue={startDateObj}
            endValue={endDateObj}
            timeValue={draftTimeValue}
            timePicker={mapFrequencyToTimePicker(selectedFrequency)}
            onStartChange={(value) => {
              setDraftStartDate(value || null);
              setLocalStartDateError('');
              setLocalTimeError('');
            }}
            onEndChange={(value) => {
              setDraftEndDate(value || null);
              setLocalEndDateError('');
            }}
            onTimeChange={(value) => {
              setDraftTimeValue(value || null);
              setLocalTimeError('');
            }}
            startDateLabel="Start date"
            endDateLabel="End date"
            timeLabel="Time"
            brandColors={brandColors}
            startDateError={localStartDateError}
            endDateError={localEndDateError}
            timeError={localTimeError}
          />
        </Suspense>
      </div>
    </PaneSheet>
  );
};

/** Renders the sheet only while `isActive`; each open mounts a fresh body (and draft). */
const ScheduledDonationsSheet = ({ isActive, ...props }) => (isActive ? <ScheduleSheetBody {...props} /> : null);

export default ScheduledDonationsSheet;
